/**
 * walleu-quantity-breaks
 * <walleu-quantity-breaks> — tiered quantity-discount cards (route A: codes).
 * - Clicking a tier sets the product quantity input (v7 quantity-input setter)
 *   and highlights the matching tier; typing a quantity highlights it too.
 * - The discount codes from data-discount-codes are applied to the cart once,
 *   the first time the quantity reaches data-min-tier-quantity. Shopify then
 *   enforces each code's own minimum-quantity requirement, so lowering the
 *   quantity later simply deactivates the discount — no cleanup needed.
 * - No vendor patching and no rewriting of the page's main price.
 */
(function () {
  if (window.customElements.get('walleu-quantity-breaks')) return;

  class WalleuQuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
      this.discountCodes = this.dataset.discountCodes || '';
      this.minTierQuantity = parseInt(this.dataset.minTierQuantity, 10) || Infinity;
      this.codesApplied = false;

      const productInfo = this.closest('.product-info');
      this.quantityInput = productInfo && productInfo.querySelector('quantity-selector input');

      this.tiers.forEach((tier) => {
        tier.addEventListener('click', () => this.selectTier(tier));
      });

      if (this.quantityInput) {
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.quantityInput.addEventListener('change', this.onQuantityChange);
      }

      // Carts built from several small adds (e.g. 2 + 2 across colors) also
      // qualify for the codes' minimums, which Shopify counts across all
      // eligible products — so watch the cart too, not just this page's input.
      this.onCartChange = this.onCartChange.bind(this);
      document.documentElement.addEventListener('cart:change', this.onCartChange);
    }

    disconnectedCallback() {
      if (this.quantityInput) {
        this.quantityInput.removeEventListener('change', this.onQuantityChange);
      }
      document.documentElement.removeEventListener('cart:change', this.onCartChange);
    }

    onCartChange(event) {
      const itemCount = event.detail && event.detail.cart && event.detail.cart.item_count;
      if (itemCount) this.maybeApplyCodes(itemCount);
    }

    selectTier(tier) {
      const quantity = parseInt(tier.dataset.quantity, 10) || 1;

      if (this.quantityInput) {
        this.quantityInput.quantity = quantity;
        // The setter does not emit events; dispatch one so every listener
        // (including our own highlight sync) follows a single code path.
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.highlight(quantity);
        this.maybeApplyCodes(quantity);
      }
    }

    onQuantityChange(event) {
      const quantity = parseInt(event.target.value, 10) || 1;
      this.highlight(quantity);
      this.maybeApplyCodes(quantity);
    }

    highlight(quantity) {
      let active = null;

      this.tiers.forEach((tier) => {
        const tierQuantity = parseInt(tier.dataset.quantity, 10) || 1;
        if (quantity >= tierQuantity && (!active || tierQuantity > parseInt(active.dataset.quantity, 10))) {
          active = tier;
        }
      });

      this.tiers.forEach((tier) => {
        tier.setAttribute('aria-pressed', tier === active ? 'true' : 'false');
      });
    }

    maybeApplyCodes(quantity) {
      if (!this.discountCodes || this.codesApplied || quantity < this.minTierQuantity) return;

      this.codesApplied = true;

      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: this.discountCodes }),
      }).catch(() => {
        this.codesApplied = false;
      });
    }
  }

  customElements.define('walleu-quantity-breaks', WalleuQuantityBreaks);
})();
